import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';

// Splits a prompt string on ```lang ... ``` fences into an ordered list of
// text/code segments. A prompt with no fences comes back as a single text
// segment, so plain questions render exactly as before.
export function parsePromptSegments(prompt) {
    // Scoped to the call (not module-level) so there's no shared `lastIndex`
    // to remember to reset between calls.
    const fence = /```([a-zA-Z0-9_+-]*)\n([\s\S]*?)```/g;
    const segments = [];
    let lastIndex = 0;
    let match;

    while ((match = fence.exec(prompt))) {
        const [full, language, code] = match;

        const text = prompt.slice(lastIndex, match.index).trim();
        if (text) segments.push({ type: 'text', text });

        segments.push({ type: 'code', language: language || null, code: code.replace(/\n$/, '') });
        lastIndex = match.index + full.length;
    }

    const rest = prompt.slice(lastIndex).trim();
    if (rest || segments.length === 0) segments.push({ type: 'text', text: rest || prompt });

    return segments;
}

function QuestionPrompt({ prompt, variant = 'h5', sx }) {
    // HostSession/StudentJoin re-render on every SSE push (e.g. each student
    // answering), not just when the question changes, so this skips re-parsing
    // the fence regex on pushes that don't touch the prompt.
    const segments = useMemo(() => parsePromptSegments(prompt), [prompt]);

    return (
        <Box sx={sx}>
            {segments.map((segment, index) =>
                segment.type === 'code' ? (
                    <Box
                        key={index}
                        component="pre"
                        sx={{
                            m: 0,
                            mb: 2,
                            p: 2,
                            textAlign: 'left',
                            fontFamily: 'source-code-pro, Menlo, Monaco, Consolas, monospace',
                            fontSize: '0.95em',
                            fontWeight: 400,
                            // Warm tan/near-black, not MUI's neutral grey.100, so this
                            // reads as part of the cream theme, not a monochrome leftover.
                            bgcolor: 'code.panel',
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 2,
                            overflowX: 'auto',
                            whiteSpace: 'pre',
                        }}
                    >
                        {segment.code}
                    </Box>
                ) : (
                    <Typography key={index} variant={variant} sx={{ fontWeight: 800, mb: 2, whiteSpace: 'pre-line' }}>
                        {segment.text}
                    </Typography>
                )
            )}
        </Box>
    );
}

export default QuestionPrompt;
